import { LazyQueryExecFunction } from '@apollo/client'
import { useCallback, useEffect, useRef, useState } from 'react'
import shallow from 'zustand/shallow'
import { SubordinateSearchQuery, SubordinateSearchQueryVariables } from '../../../types'
import { useEmployeeSearchStore } from './EmployeeSearchStore'

const clearTimer = (timer: number | undefined) => {
  if (!!timer) {
    clearTimeout(timer)
  }
}

export const useEmployeeSearchTimer = (
  callback: LazyQueryExecFunction<SubordinateSearchQuery, SubordinateSearchQueryVariables>,
  initialState: string,
): readonly [string, React.Dispatch<React.SetStateAction<string>>, () => void] => {
  const timer = useRef(0)

  const [setSearch, setSearchLabel] = useEmployeeSearchStore((state) => [state.setSearch, state.setSearchLabel], shallow)

  const [searchTerm, setSearchTerm] = useState(initialState)

  const handleSearch = useCallback(
    (value: string) => {
      clearTimer(timer.current)

      timer.current = window.setTimeout(() => {
        callback({ variables: { search: value, pagination: {} } })
        setSearchLabel(value)
        setSearch(value)
      }, 700)
    },
    [callback, setSearchLabel, setSearch],
  )

  const handleClear = useCallback(() => {
    setSearch('')
    setSearchTerm('')
    clearTimer(timer.current)
  }, [setSearch])

  useEffect(() => {
    // trigger search if search string length > 2
    const atLeastOneKeywordWithThreeOrMoreLetters = searchTerm
      .trim()
      .split(' ')
      .some((keyword) => keyword.length > 2)

    if (atLeastOneKeywordWithThreeOrMoreLetters) {
      handleSearch(searchTerm)
    } else {
      clearTimer(timer.current)
    }
  }, [searchTerm, handleSearch])

  useEffect(() => {
    return () => {
      clearTimer(timer.current)
    }
  }, [])

  return [searchTerm, setSearchTerm, handleClear] as const
}
